import type { ParamsGetMerchflowsReviews } from "@CommonApi/merchflows/reviews";
import type { ReviewStatus } from "@CommonTypes/merchflow/ReviewStatus";
import type { DateFilterModel, FilterModel } from "ag-grid-community";
import { normalizeMultiValue } from "src/merchflows/shared/multiValue";

export type SingleSelectFilterModel = {
  filterType: "singleSelect";
  value: string;
};

export type MultiSelectFilterModel = {
  filterType: "multiSelect";
  values: string[];
};

export type BackendTextFilterModel = {
  filterType: "backendTextInput";
  value: string;
};

export type BackendNumberFilterModel = {
  filterType: "backendNumberInput";
  value: number;
};

const ISO_DATE_PREFIX_REGEX = /^(\d{4})-(\d{2})-(\d{2})(?:$|[ T])/;

const isValidDateParts = (yearText: string, monthText: string, dayText: string) => {
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return false;
  }

  const parsedDate = new Date(Date.UTC(year, month - 1, day));
  return (
    parsedDate.getUTCFullYear() === year &&
    parsedDate.getUTCMonth() === month - 1 &&
    parsedDate.getUTCDate() === day
  );
};

export const toPostgresDateString = (value: string | null | undefined) => {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();
  const datePrefixMatch = trimmedValue.match(ISO_DATE_PREFIX_REGEX);

  if (datePrefixMatch) {
    const [, year, month, day] = datePrefixMatch;

    if (!isValidDateParts(year, month, day)) {
      return undefined;
    }

    return `${year}-${month}-${day}`;
  }

  return undefined;
};

const getDateFilterModel = (model: unknown): DateFilterModel | null => {
  if (!model || typeof model !== "object") {
    return null;
  }

  const dateModel = model as DateFilterModel;
  if (dateModel.filterType === "date") {
    return dateModel;
  }

  return null;
};

const getMultiSelectFilterModel = (model: unknown): MultiSelectFilterModel | null => {
  if (!model || typeof model !== "object") {
    return null;
  }

  const multiSelectModel = model as MultiSelectFilterModel;
  if (multiSelectModel.filterType === "multiSelect" && Array.isArray(multiSelectModel.values)) {
    return multiSelectModel;
  }

  return null;
};

const getTextFilterModel = (model: unknown): BackendTextFilterModel | null => {
  if (!model || typeof model !== "object") {
    return null;
  }

  const textModel = model as BackendTextFilterModel;
  if (textModel.filterType === "backendTextInput") {
    return textModel;
  }

  return null;
};

const getNumberFilterModel = (model: unknown): BackendNumberFilterModel | null => {
  if (!model || typeof model !== "object") {
    return null;
  }

  const numberModel = model as BackendNumberFilterModel;
  if (numberModel.filterType === "backendNumberInput") {
    return numberModel;
  }

  return null;
};

const readDateRange = (
  filterModel: FilterModel,
  columnId: string,
): { from?: string; to?: string } => {
  const model = getDateFilterModel(filterModel[columnId]);

  if (!model) {
    return {};
  }

  const from = toPostgresDateString(model.dateFrom);
  const to = toPostgresDateString(model.dateTo);

  if (model.type === "inRange") {
    return {
      from: from ?? to,
      to: to ?? from,
    };
  }

  return {
    from,
    to: to ?? from,
  };
};

const readExactText = (filterModel: FilterModel, columnId: string): string | undefined => {
  const model = getTextFilterModel(filterModel[columnId]);

  if (!model || typeof model.value !== "string" || model.value === "") {
    return undefined;
  }

  return model.value;
};

const readExactNumber = (filterModel: FilterModel, columnId: string): number | undefined => {
  const model = getNumberFilterModel(filterModel[columnId]);

  if (!model || typeof model.value !== "number" || !Number.isFinite(model.value)) {
    return undefined;
  }

  return model.value;
};

const readMultiSelectValues = <T extends string = string>(
  filterModel: FilterModel,
  columnId: string,
): T[] | undefined => {
  const model = getMultiSelectFilterModel(filterModel[columnId]);

  if (!model) {
    return undefined;
  }

  const values = model.values
    .filter((value): value is T => typeof value === "string" && value !== "")
    .filter((value, index, array) => array.indexOf(value) === index);

  return values.length > 0 ? values : undefined;
};

export const gridFilterModelToQueryParams = (
  filterModel: FilterModel,
): Pick<
  ParamsGetMerchflowsReviews,
  | "id"
  | "name"
  | "status"
  | "category"
  | "subcategory"
  | "goLiveDateFrom"
  | "goLiveDateTo"
  | "dateCreatedFrom"
  | "dateCreatedTo"
  | "dateModifiedFrom"
  | "dateModifiedTo"
  | "deactivationDateFrom"
  | "deactivationDateTo"
> => {
  const id = readExactNumber(filterModel, "id");
  const name = readExactText(filterModel, "name");
  const status = readMultiSelectValues<ReviewStatus>(filterModel, "status");
  const category = readMultiSelectValues(filterModel, "retailer_category_code");
  const subcategory = readMultiSelectValues(filterModel, "category_code");

  const goLiveDate = readDateRange(filterModel, "go_live_date");
  const dateCreated = readDateRange(filterModel, "created_at");
  const dateModified = readDateRange(filterModel, "updated_at");
  const deactivationDate = readDateRange(filterModel, "deactivation_date");

  return {
    id,
    name,
    status,
    category,
    subcategory,
    goLiveDateFrom: goLiveDate.from,
    goLiveDateTo: goLiveDate.to,
    dateCreatedFrom: dateCreated.from,
    dateCreatedTo: dateCreated.to,
    dateModifiedFrom: dateModified.from,
    dateModifiedTo: dateModified.to,
    deactivationDateFrom: deactivationDate.from,
    deactivationDateTo: deactivationDate.to,
  };
};

const toMultiSelectFilterModel = (
  values: string | readonly string[] | undefined,
): MultiSelectFilterModel | undefined => {
  const normalizedValues = normalizeMultiValue(values);

  if (!normalizedValues?.length) {
    return undefined;
  }

  return {
    filterType: "multiSelect",
    values: [...normalizedValues],
  };
};

const toTextFilterModel = (value: string | undefined): BackendTextFilterModel | undefined => {
  if (value === undefined || value === "") {
    return undefined;
  }

  return {
    filterType: "backendTextInput",
    value,
  };
};

const toNumberFilterModel = (value: number | undefined): BackendNumberFilterModel | undefined => {
  if (value === undefined || !Number.isFinite(value)) {
    return undefined;
  }

  return {
    filterType: "backendNumberInput",
    value,
  };
};

const toDateFilterModel = (
  from: string | undefined,
  to: string | undefined,
): DateFilterModel | undefined => {
  const normalizedFrom = toPostgresDateString(from ?? to);
  const normalizedTo = toPostgresDateString(to ?? from);

  if (!normalizedFrom && !normalizedTo) {
    return undefined;
  }

  const dateFrom = normalizedFrom || normalizedTo || "";
  const dateTo = normalizedTo || normalizedFrom || "";

  return {
    filterType: "date",
    type: "inRange",
    dateFrom: `${dateFrom} 00:00:00`,
    dateTo: `${dateTo} 00:00:00`,
  };
};

export const queryParamsToGridFilterModel = (
  queryParams: Pick<
    ParamsGetMerchflowsReviews,
    | "id"
    | "name"
    | "status"
    | "category"
    | "subcategory"
    | "goLiveDateFrom"
    | "goLiveDateTo"
    | "dateCreatedFrom"
    | "dateCreatedTo"
    | "dateModifiedFrom"
    | "dateModifiedTo"
    | "deactivationDateFrom"
    | "deactivationDateTo"
  >,
): FilterModel => {
  const filterModel: FilterModel = {};

  const idFilterModel = toNumberFilterModel(queryParams.id);
  if (idFilterModel) {
    filterModel["id"] = idFilterModel;
  }

  const nameFilterModel = toTextFilterModel(queryParams.name);
  if (nameFilterModel) {
    filterModel["name"] = nameFilterModel;
  }

  const statusFilterModel = toMultiSelectFilterModel(queryParams.status);
  if (statusFilterModel) {
    filterModel["status"] = statusFilterModel;
  }

  const categoryFilterModel = toMultiSelectFilterModel(queryParams.category);
  if (categoryFilterModel) {
    filterModel["retailer_category_code"] = categoryFilterModel;
  }

  const subcategoryFilterModel = toMultiSelectFilterModel(queryParams.subcategory);
  if (subcategoryFilterModel) {
    filterModel["category_code"] = subcategoryFilterModel;
  }

  const goLiveDateFilterModel = toDateFilterModel(
    queryParams.goLiveDateFrom,
    queryParams.goLiveDateTo,
  );
  if (goLiveDateFilterModel) {
    filterModel["go_live_date"] = goLiveDateFilterModel;
  }

  const dateCreatedFilterModel = toDateFilterModel(
    queryParams.dateCreatedFrom,
    queryParams.dateCreatedTo,
  );
  if (dateCreatedFilterModel) {
    filterModel["created_at"] = dateCreatedFilterModel;
  }

  const dateModifiedFilterModel = toDateFilterModel(
    queryParams.dateModifiedFrom,
    queryParams.dateModifiedTo,
  );
  if (dateModifiedFilterModel) {
    filterModel["updated_at"] = dateModifiedFilterModel;
  }

  const deactivationDateFilterModel = toDateFilterModel(
    queryParams.deactivationDateFrom,
    queryParams.deactivationDateTo,
  );
  if (deactivationDateFilterModel) {
    filterModel["deactivation_date"] = deactivationDateFilterModel;
  }

  return filterModel;
};
